import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DisclaimerAcceptanceService } from '../../services/disclaimer-acceptance.service';

export interface LicenseAgreementDialogData {
  disclaimerLang: string;
  disclaimerHtml: string;
  heading: string;
  buttonText: string;
  scrollHintText: string;
  saveErrorText: string;
}

@Component({
  selector: 'app-license-agreement-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './license-agreement-dialog.component.html',
  styleUrl: './license-agreement-dialog.component.scss',
})
export class LicenseAgreementDialogComponent implements AfterViewInit, OnDestroy {
  private readonly disclaimerService = inject(DisclaimerAcceptanceService);
  private readonly dialogRef = inject(MatDialogRef<LicenseAgreementDialogComponent, boolean>);

  @ViewChild('contentScroll') contentScroll?: ElementRef<HTMLDivElement>;

  canAgree = false;
  saveErrorMessage = '';
  showScrollCue = false;
  renderedDisclaimerHtml = '';

  private hasReachedBottom = false;
  private scrollIdleTimer?: ReturnType<typeof setTimeout>;
  private readonly scrollCueIdleMs = 10000;
  private cueDueAt?: number;

  constructor(@Inject(MAT_DIALOG_DATA) readonly data: LicenseAgreementDialogData) {
    this.dialogRef.disableClose = true;
    this.renderedDisclaimerHtml = this.applyHighlightMarkers(data.disclaimerHtml);
  }

  ngAfterViewInit(): void {
    // Evaluate after first paint and again after layout settles (fonts/html can shift height).
    queueMicrotask(() => {
      this.updateScrollState();
      this.updateScrollCueInitialState();
    });
    setTimeout(() => {
      this.updateScrollState();
      this.updateScrollCueInitialState();
    }, 50);
  }

  ngOnDestroy(): void {
    this.clearScrollIdleTimer();
  }

  onScroll(): void {
    if (!this.hasReachedBottom) {
      this.showScrollCue = false;
    }

    this.updateScrollState();

    if (this.hasReachedBottom) {
      this.showScrollCue = false;
      this.clearScrollIdleTimer();
      return;
    }

    this.scheduleScrollCue();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState !== 'visible' || this.hasReachedBottom || this.showScrollCue || !this.cueDueAt) {
      return;
    }

    const remainingMs = this.cueDueAt - Date.now();
    if (remainingMs <= 0) {
      this.showScrollCue = true;
      return;
    }

    this.scheduleScrollCueWithDelay(remainingMs);
  }

  agreeAndContinue(): void {
    if (!this.canAgree) {
      return;
    }

    try {
      this.disclaimerService.markAccepted(this.data.disclaimerLang);
      this.saveErrorMessage = '';
      this.dialogRef.close(true);
    } catch {
      this.saveErrorMessage = this.data.saveErrorText;
    }
  }

  private updateScrollState(): void {
    if (!this.contentScroll) {
      return;
    }

    const element = this.contentScroll.nativeElement;
    const thresholdPx = 24;
    const hasOverflow = element.scrollHeight > element.clientHeight + 1;

    // If there is no scrollable overflow, allow acceptance immediately.
    if (!hasOverflow) {
      this.canAgree = true;
      this.hasReachedBottom = true;
      return;
    }

    const reachedBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - thresholdPx;
    if (reachedBottom) {
      this.canAgree = true;
      this.hasReachedBottom = true;
    }
  }

  private updateScrollCueInitialState(): void {
    if (this.hasReachedBottom) {
      this.showScrollCue = false;
      return;
    }

    this.showScrollCue = true;
  }

  private scheduleScrollCue(): void {
    this.scheduleScrollCueWithDelay(this.scrollCueIdleMs);
  }

  private scheduleScrollCueWithDelay(delayMs: number): void {
    this.clearScrollIdleTimer();
    this.cueDueAt = Date.now() + delayMs;
    this.scrollIdleTimer = setTimeout(() => {
      if (!this.hasReachedBottom) {
        this.showScrollCue = true;
      }
    }, delayMs);
  }

  private clearScrollIdleTimer(): void {
    if (!this.scrollIdleTimer) {
      return;
    }

    clearTimeout(this.scrollIdleTimer);
    this.scrollIdleTimer = undefined;
    this.cueDueAt = undefined;
  }

  private applyHighlightMarkers(html: string): string {
    if (!html || typeof DOMParser === 'undefined') {
      return html;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const wrapper = doc.body.firstElementChild as HTMLElement | null;
      if (!wrapper) {
        return html;
      }

      const styleNodes = wrapper.querySelectorAll<HTMLElement>('[style]');
      styleNodes.forEach((node) => {
        const styleText = node.getAttribute('style') ?? '';
        if (this.isTargetHighlightColor(styleText)) {
          node.classList.add('ula-api-highlight');
        }
      });

      return wrapper.innerHTML;
    } catch {
      return html;
    }
  }

  private isTargetHighlightColor(styleText: string): boolean {
    return /background-color\s*:\s*rgb\(\s*255\s*,\s*153\s*,\s*0\s*\)/i.test(styleText);
  }
}
